import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RecommendationBadge from "../../../components/shared/RecommendationBadge";
import axiosInstance from "../../../interceptors";
import { toast } from "../../../components/shared/toast";
import {
  fetchPercentile,
  selectPercentile,
} from "../../../redux/slices/percentile-slice";

// Map pipeline verbs ("shortlist"/"reject") to the matching V022
// stage_email_template stageTo enum. Kept here so the email follow-up
// can fire from any verb-driven action without a switch.
const STAGE_FOR_VERB = { shortlist: "SHORTLISTED", reject: "REJECTED" };

const initials = (full) => {
  if (!full) return "—";
  const parts = String(full).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || "")).toUpperCase();
};

const scoreColor = (v) => {
  if (v == null) return "text-slate-400";
  if (v >= 80) return "text-emerald-600";
  if (v >= 60) return "text-amber-600";
  return "text-red-600";
};

export default function CandidateCardsRow({ candidates, jobId }) {
  if (!candidates || candidates.length === 0) return null;
  return (
    <div className="grid gap-3 mb-5"
         style={{ gridTemplateColumns: `repeat(${candidates.length}, minmax(0, 1fr))` }}>
      {candidates.map((c) => (
        <CandidateCard key={c.userId} c={c} jobId={jobId} />
      ))}
    </div>
  );
}

function CandidateCard({ c, jobId }) {
  const dispatch = useDispatch();
  const pct = useSelector(
    selectPercentile({ userId: c.userId, interviewId: c.interviewId, dimension: "overall" }),
  );
  // Initialize from server-side disposition so reload preserves the state.
  // Map server "SHORTLISTED" → local "shortlist" / "REJECTED" → "reject".
  const initialStatus = (() => {
    switch (c.dispositionStatus) {
      case "SHORTLISTED": return "shortlist";
      case "REJECTED":    return "reject";
      // OFFERED and HIRED are downstream of shortlist — show shortlisted state
      // so the buttons stay disabled but signal "already advanced".
      case "OFFERED":
      case "HIRED":       return "shortlist";
      default:            return null;
    }
  })();
  const [action, setAction] = useState({ pending: null, status: initialStatus, error: null });

  React.useEffect(() => {
    if (c.userId) {
      dispatch(fetchPercentile({ userId: c.userId, interviewId: c.interviewId, dimension: "overall" }));
    }
  }, [dispatch, c.userId, c.interviewId]);

  const topPct =
    pct && pct.percentile != null
      ? `Top ${Math.max(0, Math.round(100 - Number(pct.percentile)))}%`
      : null;

  const runAction = async (verb) => {
    if (!jobId) {
      setAction({ pending: null, status: null, error: "Missing jobId" });
      return;
    }
    setAction({ pending: verb, status: null, error: null });
    try {
      await axiosInstance.post(
        `/job-posting/pipeline/${c.userId}/${jobId}/${verb}`
      );
      setAction({ pending: null, status: verb, error: null });

      // Fire the stage-transition email after the verb succeeds. Best-effort
      // — pipeline status flip is the source of truth; an email hiccup
      // shouldn't roll it back. Skips when no email is on file. Server
      // auto-resolves feedbackUrl from the candidate's scored interview.
      const stage = STAGE_FOR_VERB[verb];
      if (stage && c.primaryEmail) {
        try {
          await axiosInstance.post(`/job-posting/stage-emails/send`, {
            stageTo: stage,
            candidateId: c.userId,
            jobId,
            recipientEmail: c.primaryEmail,
            variables: {
              candidateName: c.fullName || c.primaryEmail,
              jobTitle: `Job #${jobId}`,
              companyName: "Ample Hire",
              recruiterName: "the hiring team",
              ...(stage === "SHORTLISTED" && {
                nextStepDescription: "We'll be in touch shortly with next steps.",
              }),
            },
          });
        } catch (e) {
          toast.error(`${stage === "REJECTED" ? "Rejected" : "Shortlisted"}, but email failed`, {
            description: e?.response?.data?.message || e?.message || "Resend from the candidate report.",
          });
        }
      }
    } catch (e) {
      setAction({ pending: null, status: null, error: e?.message || "Failed" });
    }
  };

  const isShortlisted = action.status === "shortlist";
  const isRejected = action.status === "reject";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-vf-primary/15 text-vf-primary flex items-center justify-center font-semibold text-sm flex-shrink-0">
          {initials(c.fullName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-vf-base truncate" title={c.fullName}>{c.fullName}</div>
          <div className="text-[11px] text-vf-muted truncate" title={c.primaryEmail}>{c.primaryEmail}</div>
        </div>
      </div>
      <div className="flex items-baseline gap-2 mt-2">
        <div className={`text-2xl font-bold tabular-nums ${scoreColor(c.overallScore)}`}>
          {c.overallScore == null ? "—" : Number(c.overallScore).toFixed(1)}
        </div>
        <div className="text-[10px] uppercase tracking-wide text-vf-muted font-semibold">score</div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <RecommendationBadge score={c.overallScore} size="sm" />
        {topPct && (
          <span className="text-[10px] font-medium text-vf-primary bg-vf-tint px-1.5 py-0.5 rounded">
            {topPct}
          </span>
        )}
      </div>
      <div className="flex gap-1.5 mt-2.5">
        <button
          disabled={action.pending != null || isShortlisted || isRejected}
          className={`flex-1 px-2 py-1 rounded text-[11px] font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
            isShortlisted
              ? "bg-emerald-600 text-white"
              : "bg-vf-primary text-white"
          }`}
          onClick={() => runAction("shortlist")}
        >
          {action.pending === "shortlist" ? "…" : isShortlisted ? "✓ Shortlisted" : "Shortlist"}
        </button>
        <button
          disabled={action.pending != null || isShortlisted || isRejected}
          className={`flex-1 px-2 py-1 rounded text-[11px] font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
            isRejected
              ? "bg-red-600 text-white border border-red-600"
              : "border border-red-300 text-red-700 hover:bg-red-50"
          }`}
          onClick={() => runAction("reject")}
        >
          {action.pending === "reject" ? "…" : isRejected ? "✗ Rejected" : "Reject"}
        </button>
      </div>
      {action.error && (
        <div className="mt-1.5 text-[10px] text-red-700">{action.error}</div>
      )}
    </div>
  );
}
